import { memo, useCallback, useEffect, useRef, useState, useMemo } from "react";
import ReactECharts, { EChartsOption } from "echarts-for-react";
import { getStackedChartQuery } from "service/proqueryService";
import { HttpStatus, PANEL_TOKEN_ZERO_TOKEN, UnknownDevice } from "constant";
import { ChartSource } from "constant/chart";
import {
    convertPrometheusTimeToSeconds,
    formattedChartValue,
    formatValueWithUnit,
    getAccuracyFromOBJ,
    getYaxisDetails,
    isHttpSuccess,
    isIntegerChart,
    monthsToSeconds,
    parseColorToHex,
    totalDoesNotExceedMax,
    formatBinaryActuatorYAxisLabel,
} from "utils/functions";
import { debounce, get, cloneDeep, uniqBy, isEmpty } from "lodash";
import { getDeviceConfigById } from "service/gatewayService";
import "bootstrap-daterangepicker/daterangepicker.css";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import { NUMBER_OF_SCROLL } from "pages/Dashboard";
import ChartBox from "../WidgetBox";
import RefreshRateDropdown from "components/dashboard/RefreshRateDropdown";
import useChartRefreshRate from "hooks/useChartRefreshRate";
import ChartTimeSelectionDropdown from "components/dashboard/ChartTimeSelectionDropdown";
import useSelectChartTimeRange from "hooks/useSelectChartTimeRange";
import useChartWidth from "hooks/useChartWidth";
import { CustomRangeDetails } from "components/dashboard/CustomRangeDetails";
import moment from "moment";
import LocalStorageService from "service/localStorageService";
import { getCommonXAxis, getInvisibleSuffix } from "utils/chart";
import { useDateTimeFormatStore } from "hooks/useDateTimeFormatStore";
const { FORBIDDEN } = HttpStatus;
const FETCH_TIME_PER_PAGE = 500;
const DEFAULT_ACCURACY: string = "1";
const integerChartFormat = {
    minInterval: 1,
};
const nonAggBinaryChartFormat = { interval: 1 };

type StackedChartSeriesInfo = {
    uid: string;
    said: string;
    gateway: string;
    object: string;
    app: string;
    [key: string]: string;
};

type StackedChartSeries = {
    name: string;
    type: "line";
    itemStyle: { color: string };
    color?: string;
    yAxisIndex: number;
    lineStyle: { type: string | number[] };
    data: [number, string | number][];
    smooth: boolean;
    showSymbol: boolean;
    info: StackedChartSeriesInfo;
    tooltip?: { valueFormatter: (value: number) => string };
    step?: "end";
    dataFormat?: string;
    yAxisMin?: string;
    yAxisMax?: string;
};

type StackedChartState = {
    series: StackedChartSeries[];
};

// Constants for analog output constant data detection
const CONSTANT_DATA_THRESHOLD = 0.001;
const ANALOG_OUTPUT_BUFFER = 0.02;
const ANALOG_OUTPUT_INTERVAL = 0.01;

// Helper to check if a device is an actuator based on its name
const isDeviceActuator = (sensorType: string): boolean => {
    const lowerType = sensorType?.toLowerCase() || "";
    return (
        lowerType.includes("switch") ||
        lowerType.includes("relay") ||
        lowerType.includes("output") ||
        lowerType.includes("actuator") ||
        lowerType.includes("motor") ||
        lowerType.includes("valve")
    );
};

// Helper to create Y-axis label formatter
const createYAxisFormatter = (
    yAxisIndex: number,
    isActuatorChart: boolean,
    allYAxis: any[],
    distinctSeries: (StackedChartSeries | undefined)[],
    jsonList: any,
    lastTimeRangeSelect: any,
    isCorrelationChart: boolean = false
) => {
    return (value: any) => {
        const sensorType = allYAxis[yAxisIndex]?.sensorType || "";

        // For correlation charts, check each device individually
        const shouldUseActuatorFormat = isCorrelationChart
            ? isDeviceActuator(sensorType)
            : isActuatorChart;

        // For actuators, use special formatting
        if (shouldUseActuatorFormat) {
            return formatBinaryActuatorYAxisLabel(
                value,
                sensorType,
                allYAxis[yAxisIndex]?.unit
            );
        }

        // For sensors, use accuracy-based formatting
        const {
            object = "",
            app: appId = "",
            said = "",
        } = distinctSeries?.[yAxisIndex]?.info ?? {};
        const accuracy: string =
            getAccuracyFromOBJ({
                object,
                jsonList,
                said,
                appId,
                isAggregated: lastTimeRangeSelect.showAggregated,
            }) || DEFAULT_ACCURACY;

        return formatValueWithUnit(
            formattedChartValue({
                sensorAccuracy: accuracy,
                value,
            }),
            allYAxis[yAxisIndex]?.unit
        );
    };
};

// Helper to get Y-axis constraints based on chart type
export const getYAxisConstraints = (
    isActuatorChart: boolean,
    yAxis: any,
    allSeries: StackedChartSeries[],
    isNonAggregatedBinary: (info: StackedChartSeries) => boolean,
    isCorrelationChart: boolean = false
) => {
    const sensorType = yAxis?.sensorType || "";

    // For correlation charts, check each device individually
    const shouldUseActuatorFormat = isCorrelationChart
        ? isDeviceActuator(sensorType)
        : isActuatorChart;

    if (shouldUseActuatorFormat) {
        return getActuatorYAxisConstraints(
            sensorType,
            allSeries?.flatMap((s) => s?.data ?? [])
        );
    }

    if (isNonAggregatedBinary(allSeries?.[0])) {
        return nonAggBinaryChartFormat;
    }

    if (isIntegerChart(allSeries?.[0]?.dataFormat)) {
        return integerChartFormat;
    }

    return {};
};

// Helper to get Y-axis constraints for actuators
// This should match the behavior in BasicSensorChart for consistency
const getActuatorYAxisConstraints = (
    sensorType: string,
    seriesData?: any[]
) => {
    const lowerType = sensorType?.toLowerCase();

    // Binary actuators (Switch, Digital Output, Relay): only 0 and 1
    const isBinaryActuator =
        lowerType?.includes("switch") ||
        lowerType?.includes("digital output") ||
        lowerType?.includes("relay");

    const isAnalogOutput = lowerType?.includes("analog output");

    if (isBinaryActuator) {
        return { min: 0, max: 1, interval: 1 };
    }

    // Motor Controller: -1, 0, 1
    if (lowerType?.includes("motor")) {
        return { min: -1, max: 1, interval: 1 };
    }

    // For analog outputs, check if we have constant/near-zero data
    // If so, create a small range around the value (like BasicSensorChart does)
    if (isAnalogOutput && seriesData && seriesData.length > 0) {
        const values = seriesData
            .map((d: any) => Number.parseFloat(d[1]))
            .filter((v: number) => !Number.isNaN(v));
        if (values.length > 0) {
            const min = Math.min(...values);
            const max = Math.max(...values);
            const range = max - min;

            // If data is constant or very small range (similar to BasicSensorChart logic)
            if (
                range < CONSTANT_DATA_THRESHOLD ||
                (Math.abs(min) < CONSTANT_DATA_THRESHOLD &&
                    Math.abs(max) < CONSTANT_DATA_THRESHOLD)
            ) {
                const value = values[0] || 0;
                return {
                    min: value - ANALOG_OUTPUT_BUFFER,
                    max: value + ANALOG_OUTPUT_BUFFER,
                    interval: ANALOG_OUTPUT_INTERVAL,
                };
            }
        }
    }

    // Other actuators (like Generic Analog Output with varying data): no constraints
    // Return empty object to allow dynamic Y-axis calculation
    return {};
};

/** Keeps series tooltip formatting out of deep nested callbacks (Sonar nesting rule). */
const createStackedSeriesTooltipValueFormatter = (
    accuracy: string,
    unit: string | undefined
) => {
    return (value: unknown) =>
        formatValueWithUnit(
            formattedChartValue({
                sensorAccuracy: accuracy,
                value,
            }),
            unit ?? ""
        );
};

const addNoteToChart = (
    isBinary: boolean,
    isAggregated: boolean,
    option: EChartsOption,
    boxWidth: number
): EChartsOption => {
    const baseTitle = Array.isArray(option.title)
        ? { ...option.title[0] }
        : { ...option.title };

    const secondTitle = {
        text: "Averaged data may not reflect actual states. Use a shorter time range for precise state data.",
        left: "15",
        bottom: "1%",
        textStyle: {
            fontSize: 12,
            fontWeight: "normal",
            color: "#828fab",
            fontStyle: "italic",
            width: 2 * boxWidth,
            overflow: "truncate",
            ellipsis: "...",
        },
        show: isBinary && isAggregated, // toggle visibility
    };

    const update = {
        ...option,
        title: [baseTitle, secondTitle],
        legend: {
            ...option.legend,
            bottom: isBinary && isAggregated ? 50 : 33,
        },
        grid: {
            ...option.grid,
            bottom: isBinary && isAggregated ? 152 : 135,
        },

        dataZoom: option.dataZoom?.map((zoom: any) =>
            zoom.type === "slider"
                ? { ...zoom, bottom: isBinary && isAggregated ? 103 : 88 }
                : zoom
        ),
    };
    return update;
};

const StackedChart = (props: any) => {
    const jsonList = useMemo(
        () => LocalStorageService.getItem("listOfJSON"),
        []
    );
    const { dateTimeFormat, longDateTimeFormat } = useDateTimeFormatStore(
        (state) => ({
            dateTimeFormat: state.dateTimeFormat,
            longDateTimeFormat: state.longDateTimeFormat,
        })
    );

    // ----------------- props -----------------
    const {
        panel,
        panel: {
            query_time,
            time_range,
            uuid: panelId,
            refresh_rate,
            attributes,
            name,
            source,
        },
        isVisible,
        visibleRef,
        zoomLevel,
        dashboardId,
        index,
        gatewaysConfig,
    } = props;

    const isActuatorChart = source === ChartSource.MULTIPLE_ACTUATORS;
    const isCorrelationChart = source === ChartSource.SENSORS_AND_ACTUATORS;

    const chartRef: any = useRef(null);
    const chartInstance: any = chartRef.current?.getEchartsInstance();
    const zr = chartInstance?.getZr();

    const allYAxis = useMemo(() => {
        let allSensorClass: any[] = cloneDeep(attributes).map((i: any) => ({
            ...i,
            unit: i.unit === "na" ? "" : i.unit,
            time_series: i.time_series,
            sensorType: i.name,
            isActuator: isDeviceActuator(i.name),
        }));

        let uniqueYAxis = uniqBy(allSensorClass, "sensorType");

        // Show sensor yAsxis first for correlation charts
        if (isCorrelationChart) {
            uniqueYAxis.sort((a: any, b: any) => {
                return Number(a.isActuator) - Number(b.isActuator);
            });
        }

        return uniqueYAxis;
    }, [attributes, isCorrelationChart]);

    // ----------------- chart -----------------
    const [chartState, setChartState] = useState<StackedChartState>({
        series: [],
    });

    const [sensorNames, setSensorNames] = useState<Array<any>>([]);

    const { boxWidth } = useChartWidth({
        ref: visibleRef,
        occupiedWidth: 200,
    });

    const [isLoading, setIsLoading] = useState(true);
    const [selectedLegend, setSelectedLegend] = useState<
        Record<string, boolean>
    >({});
    const [isBinaryInStackedChart, setIsBinaryInStackedChart] =
        useState<boolean>(false);

    const distinctSeries = useMemo(() => {
        const series = allYAxis.map((attr: any) => {
            return chartState.series.find((series: any) => {
                const { info } = series;

                if (isEmpty(info)) {
                    return false;
                }

                return (
                    info.uid === attr.device_id &&
                    String(info.said) === String(attr.said) &&
                    info.gateway === attr.gateway_id
                );
            });
        });

        return series;
    }, [chartState.series, allYAxis]);

    // ----------------- refresh rate -----------------
    const {
        refreshRate,
        setRefreshRate,
        editableCustomRate,
        setEditableCustomRate,
    } = useChartRefreshRate(refresh_rate);

    // ----------------- time range -----------------
    const {
        lastTimeRangeSelect,
        setLastTimeRangeSelect,
        getStartDateTime,
        getEndDateTime,
        getTimeGapObject,
    } = useSelectChartTimeRange({
        initial_time_range: time_range,
        initial_query_time: query_time,
        setRefreshRate,
        dashboardId: dashboardId,
        panelId,
    });

    const isCustomRange = Boolean(lastTimeRangeSelect.query_time);
    const customRangeLayoutOffset = isCustomRange ? 25 : 0;
    const axisLegendTop = 78 + customRangeLayoutOffset;
    const gridTop = `${115 + customRangeLayoutOffset}px`;

    // ----------------- run out of token -----------------
    const [isOutOfPaymentToken, setIsOutOfPaymentToken] = useState(false);

    useEffect(() => {
        const sensorNameArray = [];

        for (let attribute of attributes) {
            const { gateway_id, device_id } = attribute;
            const sensor = gatewaysConfig?.[gateway_id]?.find(
                ({ UID }: { UID: string }) => UID === device_id
            );
            sensorNameArray.push(
                sensor?.sensor_names
                    ? sensor.sensor_names[attribute.said]
                    : UnknownDevice
            );
        }
        const _sensorNameArray = sensorNameArray.map(
            (name: string, i: number) => `${name}${getInvisibleSuffix(i)}`
        );
        setSensorNames(_sensorNameArray);
        setSelectedLegend(
            _sensorNameArray.reduce((acc: any, name: string) => {
                acc[name] = true; // default to true for all series
                return acc;
            }, {})
        );
    }, [attributes, gatewaysConfig]);

    const isNonAggregatedBinary = useCallback(
        (chartSeriesInfo: any) => {
            if (!isEmpty(chartSeriesInfo)) {
                return (
                    chartSeriesInfo?.yAxisMin === "0" &&
                    chartSeriesInfo?.yAxisMax === "1" &&
                    !lastTimeRangeSelect.showAggregated
                );
            }
            return false;
        },
        [lastTimeRangeSelect.showAggregated]
    );

    const findLargestAccuracy = useCallback(
        (chartArray: any) => {
            let largestAccuracy = "0";

            chartArray.forEach((c: any, index: any) => {
                const {
                    object,
                    app: appId,
                    said,
                } = chartState.series?.[index]?.info ?? {};

                const accuracy: string =
                    getAccuracyFromOBJ({
                        object,
                        jsonList,
                        said,
                        appId,
                        isAggregated: lastTimeRangeSelect.showAggregated,
                    }) || "0";

                if (Number(accuracy) >= Number(largestAccuracy)) {
                    largestAccuracy = accuracy;
                }
            });

            return largestAccuracy;
        },
        [lastTimeRangeSelect.showAggregated, jsonList, chartState.series]
    );

    const shouldShowAggregation = (selected_time_value: number) => {
        const sensorReportRate = lastTimeRangeSelect.INTVL;
        const totalSample = Math.floor(selected_time_value / sensorReportRate);
        return (
            selected_time_value > monthsToSeconds(6) ||
            !totalDoesNotExceedMax({ total: totalSample, numberOfChart: 1 })
        );
    };

    const getUpdatedChartConfig = async () => {
        const newLastTimeRangeSelect: any = {};

        const getSmallestSensorRate = async () => {
            let smallestSensorReportRate = 1800;

            for (let attribute of attributes) {
                const deviceConfigResponse = await getDeviceConfigById(
                    attribute.gateway_id,
                    attribute.device_id
                );
                if (isHttpSuccess(deviceConfigResponse.status)) {
                    const sensorRate = get(
                        deviceConfigResponse,
                        "data.INTVL",
                        10
                    );

                    if (sensorRate < smallestSensorReportRate) {
                        smallestSensorReportRate = sensorRate;
                    }
                }
            }

            setIsLoading(false);

            return smallestSensorReportRate;
        };

        newLastTimeRangeSelect.INTVL = await getSmallestSensorRate();
        newLastTimeRangeSelect.showCandlestickChart = false;

        return newLastTimeRangeSelect;
    };

    useEffect(() => {
        const newLastTimeRangeSelect: any = {};

        const getSmallestSensorRate = () => {
            let smallestSensorReportRate = 1800;

            for (let attribute of attributes) {
                const sensor = gatewaysConfig?.[attribute.gateway_id]?.find(
                    ({ UID }: { UID: string }) => UID === attribute.device_id
                );

                const sensorRate = get(sensor, "INTVL", 10);

                if (sensorRate < smallestSensorReportRate) {
                    smallestSensorReportRate = sensorRate;
                }
            }

            setIsLoading(false);

            return smallestSensorReportRate;
        };
        newLastTimeRangeSelect.INTVL = getSmallestSensorRate();
        newLastTimeRangeSelect.showCandlestickChart = false;

        setLastTimeRangeSelect({
            ...lastTimeRangeSelect,
            ...newLastTimeRangeSelect,
            showAggregated: shouldShowAggregation(
                convertPrometheusTimeToSeconds(lastTimeRangeSelect.time_range)
            ),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gatewaysConfig, lastTimeRangeSelect.time_range]);

    useEffect(() => {
        if (chartInstance) {
            chartInstance.setOption({
                dataZoom: [
                    {
                        type: "inside",
                        start: 0,
                        end: 100,
                        labelFormatter: (val: number) =>
                            moment(new Date(val), "x").format(dateTimeFormat),
                    },
                    {
                        type: "slider",
                        realtime: true,
                        start: 0,
                        end: 100,
                        left: 70,
                        right: 70,
                        bottom:
                            isBinaryInStackedChart &&
                                lastTimeRangeSelect.showAggregated
                                ? 103
                                : 88,
                        zoomLock: false,
                        xAxisIndex: [0, 1, 2, 3, 4, 5, 6, 7],
                        textStyle: {
                            overflow: "break",
                            width: 60,
                            fontSize: 10,
                            lineHeight: 15,
                        },
                        labelFormatter: (val: number) =>
                            moment(new Date(val), "x").format(dateTimeFormat),
                    },
                ],
                xAxis: {
                    min: Number(getStartDateTime()),
                    max: Number(getEndDateTime()),
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartInstance, dateTimeFormat, getEndDateTime, getStartDateTime]);

    const buildYAxisConfig = useCallback(() => {
        // For multiple axes (2 or more), create array of Y-axis configurations
        if (allYAxis.length >= 2) {
            return allYAxis.map((_, index: number) => {
                const allSeriesForAxis = chartState.series.filter(
                    (s) => s.yAxisIndex === index
                );

                return {
                    type: "value",
                    alignTicks:
                        index === 0
                            ? isNonAggregatedBinary(distinctSeries?.[0])
                            : false,
                    axisLabel: {
                        formatter: createYAxisFormatter(
                            index,
                            isActuatorChart,
                            allYAxis,
                            distinctSeries,
                            jsonList,
                            lastTimeRangeSelect,
                            isCorrelationChart
                        ),
                    },
                    ...getYAxisConstraints(
                        isActuatorChart,
                        allYAxis[index],
                        allSeriesForAxis,
                        isNonAggregatedBinary,
                        isCorrelationChart
                    ),
                };
            });
        }

        return {
            splitLine: {
                show: true,
                lineStyle: {
                    color: "#4B5569",
                },
            },
            axisLabel: {
                formatter: (value: any) => {
                    const yAxis = allYAxis[0];

                    // For actuators, use special formatting
                    if (isActuatorChart) {
                        const sensorType = yAxis?.sensorType ?? "";
                        return formatBinaryActuatorYAxisLabel(
                            value,
                            sensorType,
                            yAxis?.unit
                        );
                    }

                    const largestAccuracy = findLargestAccuracy(
                        chartState.series
                    );

                    return formatValueWithUnit(
                        formattedChartValue({
                            sensorAccuracy: largestAccuracy,
                            value,
                        }),
                        yAxis?.unit
                    );
                },
            },
            ...getYAxisConstraints(
                isActuatorChart,
                allYAxis[0],
                chartState.series,
                isNonAggregatedBinary,
                isCorrelationChart
            ),
        };
    }, [
        allYAxis,
        distinctSeries,
        lastTimeRangeSelect,
        jsonList,
        findLargestAccuracy,
        chartState.series,
        isNonAggregatedBinary,
        isActuatorChart,
        isCorrelationChart,
    ]);

    const memoizedYAxisConfig = useMemo(
        () => buildYAxisConfig(),
        [buildYAxisConfig]
    );

    chartInstance?.setOption(
        {
            yAxis: memoizedYAxisConfig,
        },
        { replaceMerge: ["yAxis"] }
    );

    const fetchAll = useCallback(async () => {
        if (!isVisible) return;

        const { time_range, query_time, INTVL, showAggregated } =
            lastTimeRangeSelect;

        if (sensorNames.length > 0) {
            getStackedChartQuery({
                panels: panel,
                namesAry: sensorNames,
                yAxisAry: allYAxis,
                time_range,
                query_time,
                gapTime: INTVL * 1000,
            }).then((sampleRes: any) => {
                if (sampleRes.response?.status === FORBIDDEN) {
                    if (sampleRes.config.url.includes(PANEL_TOKEN_ZERO_TOKEN)) {
                        setIsOutOfPaymentToken(true);
                    } else {
                        setIsOutOfPaymentToken(false);
                    }
                }

                const { series = [] }: { series: StackedChartSeries[] } =
                    sampleRes || {};

                series.forEach((s) => {
                    const {
                        info: { object, said, app: appId },
                    } = s ?? {};

                    const accuracy: string =
                        getAccuracyFromOBJ({
                            object,
                            jsonList,
                            said,
                            appId,
                            isAggregated: showAggregated,
                        }) || DEFAULT_ACCURACY;

                    if (s.itemStyle?.color) {
                        s.itemStyle.color = parseColorToHex(s.itemStyle.color);
                    }
                    if (s.color) {
                        s.color = parseColorToHex(s.color);
                    }
                    s.tooltip = {
                        valueFormatter:
                            createStackedSeriesTooltipValueFormatter(
                                accuracy,
                                allYAxis[s.yAxisIndex]?.unit
                            ),
                    };

                    const { yAxisMin, yAxisMax, dataFormat, isBinary } =
                        getYaxisDetails({
                            said,
                            object,
                            appId,
                        });

                    if (isBinary) {
                        s.step = "end";
                        setIsBinaryInStackedChart(true);
                    }

                    if (isIntegerChart(dataFormat)) {
                        s.dataFormat = dataFormat;
                    }

                    s.yAxisMin = yAxisMin;
                    s.yAxisMax = yAxisMax;
                });

                // Sort by yAxisIndex to match allYAxis order
                series.sort((a, b) => a.yAxisIndex - b.yAxisIndex);

                setChartState((prev) => ({
                    ...prev,
                    series,
                }));
            });
        }
    }, [
        isVisible,
        sensorNames,
        panel,
        allYAxis,
        jsonList,
        lastTimeRangeSelect,
    ]);

    const debouncedFetchAll = useMemo(
        () => debounce(fetchAll, 100),
        [fetchAll]
    );

    useEffect(() => {
        if (isOutOfPaymentToken) {
            setIsLoading(false);
            return;
        }

        if (sensorNames.length > 0) {
            debouncedFetchAll();
        }

        if (Number(refreshRate.seconds) === 0) return;

        if (!isOutOfPaymentToken) {
            // delay fetch API
            const delayTimeStamp =
                (FETCH_TIME_PER_PAGE / NUMBER_OF_SCROLL) *
                (index % NUMBER_OF_SCROLL);
            const { INTVL: sensorReportRate } = lastTimeRangeSelect;

            let getChartRefreshRateInSecs = () => {
                if (refreshRate.seconds === "auto") {
                    return sensorReportRate;
                } else {
                    return refreshRate.seconds;
                }
            };

            const interval = setInterval(() => {
                setTimeout(() => {
                    debouncedFetchAll();
                }, delayTimeStamp);
            }, getChartRefreshRateInSecs() * 1000);
            return () => clearInterval(interval);
        }

        return () => { };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isOutOfPaymentToken,
        lastTimeRangeSelect.time_range,
        lastTimeRangeSelect.query_time,
        lastTimeRangeSelect.INTVL,
        refreshRate.seconds,
        isVisible,
        isLoading,
    ]);

    useEffect(() => {
        if (!zr) {
            return;
        }

        const handleRestore = (a: any) => {
            if (a?.target?.__title === "Restore") {
                debouncedFetchAll();
            }
        };

        zr.on("click", handleRestore);

        return () => {
            zr.off("click", handleRestore);
        };
    }); // Must not add dependencies here, because we need the latest debouncedFetchAll

    const handleTimeSelectionChange = useCallback(
        ({ shouldSetAutoRefresh }: { shouldSetAutoRefresh: boolean }) => {
            // Reset chart zoom when time range changes
            if (chartInstance) {
                chartInstance.dispatchAction({
                    type: "dataZoom",
                    start: 0,
                    end: 100,
                });
            }

            if (shouldSetAutoRefresh) {
                setRefreshRate({ seconds: "auto" });
            }
        },
        [setRefreshRate, chartInstance]
    );

    const renderSubtext = useCallback(() => {
        const chartTypes = allYAxis.map((attr) => attr.sensorType);
        const [firstClassType, secondClassType] = chartTypes;

        if (!chartTypes.length) {
            return "";
        }

        if (chartTypes.length === 1) {
            return `${firstClassType ?? secondClassType}`;
        }

        return `{s1|${firstClassType} x ${secondClassType}}`;
    }, [allYAxis]);

    const axisLegendGraphic = useMemo(() => {
        const chartTypes = allYAxis.map((attr) => attr.sensorType);
        if (chartTypes.length !== 2) {
            return [];
        }

        const [firstClassType, secondClassType] = chartTypes;
        const axisLabelStyle = {
            fill: "#ffffff",
            font: "400 18px Roboto",
            width: (boxWidth + 150) / 2,
            overflow: "truncate",
            ellipsis: "...",
        };

        return [
            {
                type: "text",
                left: 14,
                top: axisLegendTop,
                style: {
                    ...axisLabelStyle,
                    text: `— ${firstClassType}`,
                },
            },
            {
                type: "text",
                right: 14,
                top: axisLegendTop,
                style: {
                    ...axisLabelStyle,
                    text: `- - - ${secondClassType}`,
                    textAlign: "left",
                },
            },
        ];
    }, [allYAxis, axisLegendTop, boxWidth]);

    const getYAxisOptions = () => buildYAxisConfig();

    const option: any = {
        backgroundColor: "#2F3A51",
        responsive: true,
        maintainAspectRatio: false,
        tooltip: {
            trigger: "axis",
            renderMode: "html",
            appendTo: "body",
            axisPointer: {
                label: {
                    formatter: (params: any) => {
                        return `${moment(params.value, "x").format(
                            longDateTimeFormat
                        )}`;
                    },
                },
            },
        },
        title: {
            show: true,
            left: 14,
            top: 15,
            text: name || "Multiple Sensor Chart",
            textStyle: {
                color: "#ffffff",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: 18,
                overflow: "truncate",
                ellipsis: "...",
                width: boxWidth,
            },
            subtext: renderSubtext(),
            subtextStyle: {
                color: "#828FAB",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: 14,
                width: boxWidth,
                overflow: "truncate",
                ellipsis: "...",
                rich: {
                    s1: {
                        overflow: "truncate",
                        ellipsis: "...",
                        color: "#828fab",
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: 14,
                    },
                },
            },
        },
        graphic: axisLegendGraphic,
        toolbox: {
            feature: {
                dataZoom: {
                    yAxisIndex: "none",
                    title: {
                        back: "Zoom Out",
                    },
                },
                restore: {},
                saveAsImage: {
                    icon: "path://M8.79999 3.71991C7.92975 3.71991 7.07906 3.97797 6.35548 4.46144C5.63191 4.94492 5.06795 5.63211 4.73493 6.4361C4.4019 7.2401 4.31477 8.12479 4.48454 8.97831C4.65432 9.83182 5.07337 10.6158 5.68872 11.2312C6.30407 11.8465 7.08808 12.2656 7.94159 12.4354C8.79511 12.6051 9.6798 12.518 10.4838 12.185C11.2878 11.852 11.975 11.288 12.4584 10.5644C12.9419 9.84084 13.2 8.99015 13.2 8.11991C13.2 6.95296 12.7364 5.8338 11.9113 5.00864C11.0861 4.18348 9.96694 3.71991 8.79999 3.71991ZM8.79999 11.6399C8.1038 11.6399 7.42324 11.4335 6.84438 11.0467C6.26552 10.6599 5.81436 10.1101 5.54794 9.46695C5.28152 8.82376 5.21181 8.116 5.34763 7.43319C5.48345 6.75038 5.8187 6.12317 6.31098 5.63089C6.80326 5.13861 7.43046 4.80336 8.11327 4.66754C8.79608 4.53172 9.50384 4.60143 10.147 4.86785C10.7902 5.13427 11.34 5.58544 11.7268 6.1643C12.1135 6.74316 12.32 7.42372 12.32 8.11991C12.32 9.05347 11.9491 9.9488 11.289 10.6089C10.6289 11.2691 9.73355 11.6399 8.79999 11.6399Z M14.52 1.95998H12.5928L12.2276 1.22958C12.0602 0.930245 11.8192 0.678578 11.5274 0.498406C11.2356 0.318234 10.9026 0.215518 10.56 0.199982H7.04C6.69739 0.215518 6.36442 0.318234 6.0726 0.498406C5.78079 0.678578 5.53978 0.930245 5.3724 1.22958L5.0072 1.95998H3.08C2.26313 1.95998 1.47972 2.28448 0.902111 2.86209C0.324499 3.4397 0 4.22311 0 5.03998V12.08C0 12.8968 0.324499 13.6803 0.902111 14.2579C1.47972 14.8355 2.26313 15.16 3.08 15.16H14.52C15.3369 15.16 16.1203 14.8355 16.6979 14.2579C17.2755 13.6803 17.6 12.8968 17.6 12.08V5.03998C17.6 4.22311 17.2755 3.4397 16.6979 2.86209C16.1203 2.28448 15.3369 1.95998 14.52 1.95998ZM16.72 12.08C16.72 12.6635 16.4882 13.223 16.0756 13.6356C15.663 14.0482 15.1035 14.28 14.52 14.28H3.08C2.49652 14.28 1.93694 14.0482 1.52436 13.6356C1.11178 13.223 0.88 12.6635 0.88 12.08V5.03998C0.88 4.4565 1.11178 3.89693 1.52436 3.48435C1.93694 3.07177 2.49652 2.83998 3.08 2.83998H5.28C5.36125 2.83973 5.44085 2.81699 5.50997 2.77428C5.57909 2.73156 5.63503 2.67054 5.6716 2.59798L6.16 1.62558C6.25235 1.47089 6.38047 1.34061 6.53359 1.24568C6.68671 1.15074 6.86037 1.09392 7.04 1.07998H10.56C10.7396 1.09392 10.9133 1.15074 11.0664 1.24568C11.2195 1.34061 11.3476 1.47089 11.44 1.62558L11.9284 2.59798C11.965 2.67054 12.0209 2.73156 12.09 2.77428C12.1591 2.81699 12.2387 2.83973 12.32 2.83998H14.52C15.1035 2.83998 15.663 3.07177 16.0756 3.48435C16.4882 3.89693 16.72 4.4565 16.72 5.03998V12.08Z",
                },
            },
            top: 12,
            right: 50,
            emphasis: {
                iconStyle: {
                    textPosition: "top",
                    textPadding: -4,
                },
            },
            iconStyle: {
                borderColor: "#00CBAF",
            },
        },
        legend: {
            type: "scroll",
            selectedMode: true,
            left: 15,
            bottom: 33,
            textStyle: {
                color: "#ffffff",
                fontFamily: "Roboto",
                fontWeight: 400,
            },
            icon: "circle",
            pageTextStyle: {
                color: "#ffffff",
            },
            pageIconInactiveColor: "#6c6d6e",
            pageIconColor: "#c8c6c6",
            selected: selectedLegend,
            data: (chartState.series ?? []).map((s: any) => {
                return {
                    name: s.name,
                    itemStyle: {
                        color: s.itemStyle?.color ?? s.color,
                    },
                };
            }),
        },
        grid: {
            left: 25,
            top: gridTop,
            right: 25,
            bottom: "135px",
            // Rather than set the fix left and right based on allYAxis.length,
            // we set the containLabel to true to let the grid size to be adjusted automatically.
            containLabel: true,
        },
        xAxis: {
            type: "time",
            boundaryGap: false,
            axisLine: {
                lineStyle: {
                    color: "#4B5569",
                },
            },
            splitNumber: "3",
        },
        yAxis: getYAxisOptions(),
        series: chartState.series,
    };

    const updatedOption = addNoteToChart(
        isBinaryInStackedChart,
        lastTimeRangeSelect.showAggregated,
        option,
        boxWidth
    );

    const chartOptions = useMemo(() => {
        return {
            ...updatedOption,
            xAxis: getCommonXAxis(option),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updatedOption]);

    return (
        <ChartBox
            ref={visibleRef}
            className={
                zoomLevel <= 0.5
                    ? "widget-box w-100 h-30vh"
                    : "widget-box w-100"
            }
        >
            <div className="widget">
                <ChartTimeSelectionDropdown
                    lastTimeRangeSelect={lastTimeRangeSelect}
                    setLastTimeRangeSelect={setLastTimeRangeSelect}
                    getStartDateTime={getStartDateTime}
                    getEndDateTime={getEndDateTime}
                    dashboardId={dashboardId}
                    panelId={panelId}
                    getTimeGapObject={getTimeGapObject}
                    getUpdatedChartConfig={getUpdatedChartConfig}
                    onChange={handleTimeSelectionChange}
                />
                {lastTimeRangeSelect.query_time ? (
                    <CustomRangeDetails
                        lastTimeRangeSelect={lastTimeRangeSelect}
                    />
                ) : (
                    <></>
                )}
                <div
                    className="widget-tool"
                    style={{
                        bottom:
                            isBinaryInStackedChart &&
                                lastTimeRangeSelect.showAggregated
                                ? "30px"
                                : "12px",
                    }}
                >
                    <RefreshRateDropdown
                        refreshRate={refreshRate}
                        setRefreshRate={setRefreshRate}
                        editableCustomRate={editableCustomRate}
                        setEditableCustomRate={setEditableCustomRate}
                        dashboardId={dashboardId}
                        panelId={panelId}
                        lastTimeRangeSelect={lastTimeRangeSelect}
                    />
                </div>
                <ContentWrapper isLoading={isLoading || !isVisible}>
                    <ReactECharts
                        ref={chartRef}
                        key={panelId}
                        className="chart-box"
                        option={chartOptions}
                        theme={"dark"}
                        lazyUpdate={true}
                        onChartReady={(chartInstance) => {
                            chartInstance.on(
                                "legendselectchanged",
                                function (changes: any) {
                                    setSelectedLegend(changes.selected);
                                }
                            );
                        }}
                    />
                </ContentWrapper>
            </div>
        </ChartBox>
    );
};

export default memo(StackedChart);
